/**
 * Edem AI Billing Configuration
 *
 * Subscription Passes:
 * 1. Weekly Pass - $2.99/week, 500 coins/week
 * 2. Monthly Pass - $5.99/month, 1500 coins/month
 * 3. 3-Month Bundle - $14.99/3 months, 7500 coins total
 *
 * Coin Top-Ups (one-time purchases):
 * - Various coin packs from 250 to 18,000 coins
 */
import { BillingProviderSchema, createBillingSchema } from '@kit/billing';

// The billing provider to use (Stripe for Edem AI)
const provider = BillingProviderSchema.parse(
  process.env.NEXT_PUBLIC_BILLING_PROVIDER ?? 'stripe',
);

export default createBillingSchema({
  provider,
  products: [
    // ============================================
    // SUBSCRIPTION PASSES
    // ============================================

    // Weekly Pass - €2.99/week, 500 coins
    {
      id: 'pass-weekly',
      name: 'Weekly Pass',
      description: '500 coins per week',
      currency: 'EUR',
      plans: [
        {
          name: 'Weekly Pass',
          id: 'pass-weekly',
          paymentType: 'recurring',
          interval: 'week',
          // 3-day trial (shortened from 7 on 2026-08-31 — a week of free
          // unlimited access was mostly harvesting cancel-on-day-one users).
          //
          // NOTE: at 7 days this plan hit buildPriceArray's collapse branch
          // (trial length == cadence -> ONE delayed recurring entry). At 3 it
          // no longer does, so weekly now emits the same two-entry chain as
          // monthly and quarterly: [{3d once}, {7d repeat}]. That shape is the
          // one already proven in production by real purchases, but it is NEW
          // for this plan — probed against live /paymentPage before shipping.
          trialDays: 3,
          lineItems: [
            {
              id: 'pri_01kjrdqvxck2a0x5s0f5d97ce4',
              name: 'Weekly Pass',
              cost: 2.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: [
        '500 coins per week',
        'Up to 50 image unblurs',
        'Up to 20 image generations',
        'Unlimited conversations',
        'Access to all characters',
      ],
    },

    // Monthly Pass - $5.99/month
    {
      id: 'pass-monthly',
      name: 'Monthly Pass',
      description: '1,500 coins per month',
      currency: 'EUR',
      badge: 'Most Popular',
      highlighted: true,
      plans: [
        {
          name: 'Monthly Pass',
          id: 'pass-monthly',
          paymentType: 'recurring',
          interval: 'month',
          // 3-day trial. buildPriceArray emits CentroBill's own documented
          // trial shape — the FULL price delayed by the trial window,
          // [{offset 3d, once}, {offset 30d, repeat}]. Their docs are explicit
          // that this is the mechanism: "no charge occurs initially ... no
          // zero-amount or free transaction is needed; the delay itself
          // creates the trial period."
          //
          // PROBATION CLEARED 2026-08-30. The 2026-08-19 fear — that this
          // shape charges at t0 rather than at trial end — is disproven by a
          // real customer: sub 323016538 produced `charge/auth EUR 5.99` then
          // `credit/void` twice, EUR 0.00 actually captured, coins granted,
          // period exactly the trial length. The trial is genuinely free, so
          // the UI is allowed to promise one.
          //
          // SHORTENED 7 -> 3 on 2026-08-31. Both trials taken under the 7-day
          // window cancelled auto-renew almost immediately (one after ~2
          // minutes) while keeping a full free week plus the whole coin
          // allotment. Three days keeps the "try before you pay" promise
          // without funding a week of unlimited use for users who already
          // opted out.
          trialDays: 3,
          lineItems: [
            {
              id: 'pri_01kjrdr2wwpch7c1bttkqm7srf',
              name: 'Monthly Pass',
              cost: 5.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: [
        '1,500 coins per month',
        'Up to 150 image unblurs',
        'Up to 60 image generations',
        'Unlimited conversations',
        'Access to all characters',
        'Priority support',
      ],
    },

    // 3-Month Bundle - €14.99 every 3 months (recurring, auto-renew)
    {
      id: 'pass-quarterly',
      name: '3-Month Bundle',
      description: '7,500 coins every 3 months',
      currency: 'EUR',
      badge: 'Best Value',
      plans: [
        {
          name: '3-Month Bundle',
          id: 'pass-quarterly',
          paymentType: 'recurring',
          interval: 'month',
          intervalCount: 3,
          // See the note on Monthly Pass. Chains 3d then 90d, same as monthly.
          trialDays: 3,
          lineItems: [
            {
              id: 'pri_01kjrdrc98e4h5cqr2x0bb2xd2',
              name: '3-Month Bundle',
              cost: 14.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: [
        '7,500 coins every 3 months',
        'Up to 250 image unblurs/month',
        'Up to 100 image generations/month',
        'Unlimited conversations',
        'Access to all characters',
        'Priority support',
        'Auto-renews every 3 months · save 17% vs monthly',
      ],
    },

    // ============================================
    // COIN TOP-UP PACKS
    // ============================================

    // coins-250 ($1.99) removed 2026-09-05 — too small to be worth selling.
    // COIN_PACK_AMOUNTS keeps its mapping so historical IPNs still resolve.

    // 700 Coins - $4.99
    {
      id: 'coins-700',
      name: '700 Coins',
      description: 'Medium coin pack',
      currency: 'EUR',
      plans: [
        {
          name: '700 Coins',
          id: 'coins-700',
          paymentType: 'one-time',
          lineItems: [
            {
              id: 'pri_01kjrdrwqjeht4s5f74shnawwa',
              name: '700 Coins',
              cost: 4.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: ['700 coins', '140 chat unlocks', '70 image unlocks'],
    },

    // 1,600 Coins - $9.99
    {
      id: 'coins-1600',
      name: '1,600 Coins',
      description: 'Large coin pack',
      currency: 'EUR',
      badge: 'Popular',
      plans: [
        {
          name: '1,600 Coins',
          id: 'coins-1600',
          paymentType: 'one-time',
          lineItems: [
            {
              id: 'pri_01kjrdrwxccbc7q5xyd5bvdv1f',
              name: '1,600 Coins',
              cost: 9.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: ['1,600 coins', '320 chat unlocks', '160 image unlocks'],
    },

    // 3,500 Coins - $19.99
    {
      id: 'coins-3500',
      name: '3,500 Coins',
      description: 'XL coin pack',
      currency: 'EUR',
      plans: [
        {
          name: '3,500 Coins',
          id: 'coins-3500',
          paymentType: 'one-time',
          lineItems: [
            {
              id: 'pri_01kjrdrx0b34mv67445pc4e58h',
              name: '3,500 Coins',
              cost: 19.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: ['3,500 coins', '700 chat unlocks', '350 image unlocks'],
    },

    // 8,000 Coins - $39.99
    {
      id: 'coins-8000',
      name: '8,000 Coins',
      description: 'Mega coin pack',
      currency: 'EUR',
      badge: 'Best Value',
      plans: [
        {
          name: '8,000 Coins',
          id: 'coins-8000',
          paymentType: 'one-time',
          lineItems: [
            {
              id: 'pri_01kjrdrws5wm5qsydwer1dydex',
              name: '8,000 Coins',
              cost: 39.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: ['8,000 coins', '1,600 chat unlocks', '800 image unlocks'],
    },

    // 18,000 Coins - $79.99
    {
      id: 'coins-18000',
      name: '18,000 Coins',
      description: 'Ultimate coin pack',
      currency: 'EUR',
      plans: [
        {
          name: '18,000 Coins',
          id: 'coins-18000',
          paymentType: 'one-time',
          lineItems: [
            {
              id: 'pri_01kjrdrws6w46zdkkq8h4b9baf',
              name: '18,000 Coins',
              cost: 79.99,
              type: 'flat' as const,
            },
          ],
        },
      ],
      features: ['18,000 coins', '3,600 chat unlocks', '1800 image unlocks'],
    },
  ],
});

/**
 * Pass Tier Configuration
 * Defines benefits for each pass type
 */
export type PassTier = 'free' | 'weekly' | 'monthly' | 'quarterly';

export interface PassBenefits {
  tier: PassTier;
  coinsPerPeriod: number;
  maxUnblursPerPeriod: number;
  maxImageGensPerPeriod: number;
  unlimitedConversations: boolean;
  prioritySupport: boolean;
}

export const PASS_TIERS: Record<PassTier, PassBenefits> = {
  free: {
    tier: 'free',
    coinsPerPeriod: 0,
    maxUnblursPerPeriod: 0,
    maxImageGensPerPeriod: 0,
    unlimitedConversations: false,
    prioritySupport: false,
  },
  weekly: {
    tier: 'weekly',
    coinsPerPeriod: 500,
    maxUnblursPerPeriod: 50,
    maxImageGensPerPeriod: 20,
    unlimitedConversations: true,
    prioritySupport: false,
  },
  monthly: {
    tier: 'monthly',
    coinsPerPeriod: 1500,
    maxUnblursPerPeriod: 150,
    maxImageGensPerPeriod: 60,
    unlimitedConversations: true,
    prioritySupport: true,
  },
  quarterly: {
    tier: 'quarterly',
    coinsPerPeriod: 2000, // per month, 7500 total over 3 months
    maxUnblursPerPeriod: 250,
    maxImageGensPerPeriod: 100,
    unlimitedConversations: true,
    prioritySupport: true,
  },
};

/**
 * Map plan IDs to pass tiers
 */
export const PLAN_TO_TIER: Record<string, PassTier> = {
  'pass-weekly': 'weekly',
  'pass-monthly': 'monthly',
  'pass-quarterly': 'quarterly',
};

// Legacy type alias for backwards compatibility
export type SubscriptionTier = PassTier;
export type TierBenefits = PassBenefits;
export const SUBSCRIPTION_TIERS = PASS_TIERS;

/**
 * Get pass tier from plan ID
 */
export function getTierFromPlanId(planId: string): PassTier {
  return PLAN_TO_TIER[planId] ?? 'free';
}

/**
 * Get tier benefits
 */
export function getTierBenefits(tier: PassTier): PassBenefits {
  return PASS_TIERS[tier];
}

/**
 * Check if a plan ID is a pass subscription
 */
export function isPassPlan(planId: string): boolean {
  return planId.startsWith('pass-');
}

// Legacy alias for backwards compatibility
export const isSubscriptionPlan = isPassPlan;

/**
 * Coin Prices (defaults, actual values from database coin_prices table).
 *
 * Every site that charges or displays a price MUST go through
 * getActionPrice()/getActionPriceAdmin() so the admin panel
 * (/admin/coin-pricing) is the single source of truth at runtime. These
 * defaults are the fallback used only when the DB row is missing.
 */
export const DEFAULT_COIN_PRICES = {
  image_unblur: 10, // Unblur an image
  image_generation: 25, // Generate 1 image (standard)
  image_generation_high: 35, // High-quality image (sharper / better details)
  image_generation_premium: 50, // Premium image (best quality, slowest)
  video_generation_fast: 60, // Short video — 3s clip (~1 min)
  video_generation: 100, // Standard video — 5s clip (~2-3 min)
  video_generation_long: 200, // Long video — 7s clip (~5 min)
  video_generation_premium: 200, // Premium video (H100)
  video_generation_elite: 300, // Elite video (H200)
  audio_generation: 15, // Generate audio message
  character_creation: 50, // Create a custom character
  chat_unlock: 5, // Start a new chat with a character
  video_call_minute: 60, // 1 minute of live video call (avatar + voice)
  voice_call_minute: 15, // 1 minute of live voice call (audio only)
} as const;

export type CoinActionType = keyof typeof DEFAULT_COIN_PRICES;

/**
 * Coin pack amounts - coins granted for each one-time purchase plan
 * Used by billing webhook to credit coins after purchase
 */
export const COIN_PACK_AMOUNTS: Record<string, number> = {
  'coins-250': 250,
  'coins-700': 700,
  'coins-1600': 1600,
  'coins-3500': 3500,
  'coins-8000': 8000,
  'coins-18000': 18000,
  // pass-quarterly used to be a one-time bundle and shipped here so legacy
  // /paymentPage IPNs that already exist in flight still credit coins.
  // New pass-quarterly purchases are recurring subscriptions — see
  // SUBSCRIPTION_COINS below for the per-cycle grant.
  'pass-quarterly': 7500,
};

/**
 * Subscription coins - coins granted each billing cycle
 * Used by billing webhook on subscription renewal
 */
export const SUBSCRIPTION_COINS: Record<string, number> = {
  'pass-weekly': 500, // 500 coins per week
  'pass-monthly': 1500, // 1,500 coins per month
  'pass-quarterly': 7500, // 7,500 coins per 3-month cycle (auto-renews)
};

// Legacy alias for backwards compatibility
export const SUBSCRIPTION_MONTHLY_COINS = SUBSCRIPTION_COINS;

/**
 * Signup bonus - coins granted when a new user registers
 */
export const SIGNUP_BONUS_COINS = 100;
